/* The size-and-pack sheet (#qsheet): opened from a card's quick add, the mobile sticky bar or any
   element with data-sheet-open inside a product-json host. Size never pre-selected; the pack defaults to
   the most-chosen rung; refuses to add without a size; ends in the cart drawer.
   It also picks the size of a sized add-on on the product page and of a sized product in the cart's
   "Goes with it" rail, so neither goes in the cart in a size nobody chose. */
import { $, $$, layers, money, fmt, track, toast, unitLabel, unitKey } from '@theme/utils';
import { ProductModel, readProductJSON } from '@theme/product-data';
import { cart } from '@theme/cart';

const T = () => window.Theme;
const sheet = () => $('#qsheet');
let model = null, sel = [], ctx = 'card', host = null, onPick = null, source = 'sheet';

function findHost(trigger) {
  const explicit = trigger.dataset.sheetSource && $(trigger.dataset.sheetSource);
  return explicit || trigger.closest('.card, .buy, .lander-offer, [data-product-host]') || $('[data-product-host]');
}

/* A product in the storefront's JSON (/products/<handle>.js, product recommendations) as the model the sheet reads.
   The pack and colour options are found by the names in theme settings, as snippets/product-json finds them. */
export function modelFromAjax(p) {
  if (!p || !Array.isArray(p.variants) || !p.variants.length) return null;
  const names = (list) => (list || []).map((s) => String(s).trim().toLowerCase());
  const packs = names(T().settings.packOptionNames), colors = names(T().settings.colorOptionNames);
  const options = (p.options || []).map((o, i) => {
    const name = String(o && o.name != null ? o.name : o).trim();
    const values = Array.isArray(o && o.values) ? o.values.map(String) : [...new Set(p.variants.map((v) => (v.options || [])[i]).filter((x) => x != null))];
    return { name, values };
  });
  return new ProductModel({
    id: p.id, handle: p.handle, title: p.title, url: p.url || `/products/${p.handle}`,
    /* the unit key from the tag, the handle and the title, as the product page finds it (snippets/unit-key) */
    unit: unitKey(p),
    packIndex: options.findIndex((o) => packs.includes(o.name.toLowerCase())),
    colorIndex: options.findIndex((o) => colors.includes(o.name.toLowerCase())),
    options,
    variants: p.variants.map((v) => ({ id: v.id, price: v.price, compare: v.compare_at_price || null, available: v.available, sku: v.sku, options: v.options || [], image: v.featured_image ? v.featured_image.src : null })),
  });
}

/* opts: model or product (storefront JSON) instead of a product-json host; title; packQty, the pack to start on
   (else the most-chosen); sel, a selection to start from; source for analytics; onPick(variant, selection, model):
   the sheet only picks, hands the variant back and closes, nothing goes in the cart. */
function open(trigger, opts = {}) {
  const sh = sheet(); if (!sh) return;
  const given = opts.model || (opts.product ? modelFromAjax(opts.product) : null);
  host = given ? null : findHost(trigger);
  model = given || (host ? readProductJSON(host) : null);
  if (!model) { if (host && host.querySelector('a[href]')) location.href = host.querySelector('a[href]').href; return; }
  if (opts.title) model.d.title = opts.title;
  onPick = typeof opts.onPick === 'function' ? opts.onPick : null;
  source = opts.source || 'sheet';
  ctx = onPick ? 'pick' : (host && document.querySelector('.buy') && host.closest('.buy, .lander-offer') ? 'pdp' : 'card');
  const pdp = window.NOCRY && window.NOCRY.pdp;
  if (ctx === 'pdp' && pdp) sel = pdp.getSelection().slice();
  else if (Array.isArray(opts.sel) && opts.sel.length === model.options.length) sel = opts.sel.slice();
  else {
    sel = model.options.map((o, i) => {
      if (i === model.packIndex) { const l = model.ladder(model.options.map(() => null)); const def = (opts.packQty && l.find((r) => r.qty === opts.packQty)) || l.find((r) => r.mostChosen) || l[0]; return def ? def.variant.options[i] : o.values[0]; }
      if (i === model.colorIndex) return o.values[0];
      return o.values.length === 1 ? o.values[0] : null;
    });
  }
  render();
  layers.open('sheet', sh);
  track('sheet_opened', { product: model.d.handle, source: trigger.dataset.sheetOpen || opts.source || 'card' });
}
function close() { layers.close('sheet'); }

function render() {
  const sh = sheet(); const s = T().strings;
  sh.querySelector('[data-sheet-name]').textContent = model.d.title;
  sh.querySelector('[data-sheet-sub]').textContent = model.d.sub || '';
  const opts = sh.querySelector('[data-sheet-options]');
  opts.innerHTML = model.pickOptions.map((o) => {
    const chips = o.values.map((v) => {
      const probe = sel.slice(); probe[o.index] = v;
      if (model.packIndex >= 0) probe[model.packIndex] = null;
      const avail = model.match(probe).some((x) => x.available);
      return `<label class="chip${sel[o.index] === v ? ' on' : ''}${avail ? '' : ' unavailable'}"><input type="radio" name="sheet-opt-${o.index}" value="${v.replace(/"/g, '&quot;')}" data-opt="${o.index}" ${sel[o.index] === v ? 'checked' : ''}>${v}</label>`;
    }).join('');
    return `<div class="sheet-row"><span class="lb2">${o.name}${sel[o.index] ? `: <span class="small muted" style="text-transform:none;letter-spacing:0">${sel[o.index]}</span>` : ''}</span><div class="chips" role="radiogroup" aria-label="${o.name}">${chips}</div></div>`;
  }).join('');
  const packRow = sh.querySelector('[data-sheet-packrow]');
  const ladder = model.packIndex >= 0 ? model.ladder(sel) : [];
  if (!ladder.length) packRow.hidden = true;
  else {
    packRow.hidden = false;
    const cur = sel[model.packIndex];
    sh.querySelector('[data-sheet-packs]').innerHTML = ladder.map((r) => {
      const tag = model.tag(r); const on = r.variant.options[model.packIndex] === cur;
      return `<label class="pkc${on ? ' on' : ''}${tag ? ' has-tag' : ''}${r.available ? '' : ' unavailable'}"><input type="radio" name="sheet-pack" value="${r.variant.options[model.packIndex].replace(/"/g, '&quot;')}" data-opt="${model.packIndex}" ${on ? 'checked' : ''}>${tag ? `<i class="tag">${tag}</i>` : ''}<b>${unitWord(r.qty)}</b><span>${money(r.price)}</span><small>${model.meta(r).replace(/<[^>]+>/g, '')}</small></label>`;
    }).join('');
  }
  const v = model.find(sel);
  const row = ladder.find((r) => v && r.variant.id === v.id) || (v ? { qty: v.qty, price: v.price } : null);
  sh.querySelector('[data-sheet-line]').textContent = row ? (row.qty > 1 ? `${unitWord(row.qty)} · ${money(Math.round(row.price / row.qty))} ${s.each}` : unitWord(1)) : '';
  sh.querySelector('[data-sheet-total]').textContent = row ? money(row.price) : '';
  const inst = sh.querySelector('[data-sheet-inst]');
  inst.textContent = row && row.price >= (Number(T().settings.installmentsMin) || 50) * 100 ? fmt(s.installments, { amount: money(Math.round(row.price / 4)) }) : '';
  const err = sh.querySelector('[data-sheet-err]'); err.classList.remove('show'); err.textContent = '';
  const btn = sh.querySelector('[data-sheet-atc]');
  /* the button's own words are kept before any render changes them; picking for an add-on reads "Choose L" */
  if (!btn.dataset.label) btn.dataset.label = btn.textContent;
  let label = btn.dataset.label;
  if (ctx === 'pick') {
    const chosen = model.sizeOptions.map((o) => sel[o.index]).filter((x) => x != null).join(' · ');
    label = fmt(s.chooseX, { value: chosen || (model.sizeOptions[0] ? model.sizeOptions[0].name.toLowerCase() : '') });
  }
  btn.textContent = v && !v.available ? s.soldOut : label;
}
function unitWord(n) { return unitLabel(n, model.unit); }

function missing() {
  return model.pickOptions.filter((o) => o.index !== model.colorIndex && sel[o.index] == null);
}

async function addToCart() {
  const sh = sheet(); const s = T().strings;
  const miss = missing();
  if (miss.length) {
    const err = sh.querySelector('[data-sheet-err]');
    /* the size option by name, English or Spanish (Size; Talla, Tamaño) */
    err.textContent = /size|talla|tamaño/.test(miss[0].name.toLowerCase()) ? s.chooseSize : fmt(s.chooseOption, { option: miss[0].name.toLowerCase() });
    err.classList.add('show');
    const first = sh.querySelector(`input[data-opt="${miss[0].index}"]`); if (first) first.focus();
    return;
  }
  const v = model.find(sel);
  if (!v) return;
  if (!v.available) { toastErr(s.soldOut); return; }
  if (ctx === 'pick') { const pick = onPick; onPick = null; close(); if (pick) pick(v, sel.slice(), model); return; }
  const btn = sh.querySelector('[data-sheet-atc]'); btn.setAttribute('aria-busy', 'true');
  if (ctx === 'pdp' && window.NOCRY && window.NOCRY.pdp) {
    window.NOCRY.pdp.setSelection(sel);
    close();
    await window.NOCRY.pdp.addToCart('sticky');
  } else {
    const ok = await cart.add([{ id: v.id, quantity: 1 }], { source, destination: document.body.classList.contains('lander') ? 'lander' : 'card' });
    if (ok) close();
  }
  btn.removeAttribute('aria-busy');
}
function toastErr(msg) { const err = sheet().querySelector('[data-sheet-err]'); err.textContent = msg; err.classList.add('show'); }

/* ---------- sized add-ons on the product page ----------
   An add-on switch adds one variant along with the main product. For a product with a size (gloves) that variant
   would be the first size, so switching such an add-on on opens this sheet: the shopper picks the size (and the
   pack) and the switch turns on with that variant. One-size add-ons still switch on in one tap. The add-on's
   options come from its storefront JSON, fetched once per page and ahead of time after the page loads. */
const productJSON = {};
function fetchProduct(url) {
  if (!url) return Promise.reject(new Error('no product link'));
  let path;
  try { path = new URL(url, location.origin).pathname.replace(/^(.*?\/products\/[^/]+).*$/, '$1'); } catch (e) { return Promise.reject(e); }
  if (!/\/products\/[^/]+$/.test(path)) return Promise.reject(new Error('not a product link'));
  if (!productJSON[path]) {
    productJSON[path] = fetch(`${path}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .catch((err) => { delete productJSON[path]; throw err; });
  }
  return productJSON[path];
}
const addonRow = (sw) => sw.closest('[data-addon]');
const addonLink = (sw) => { const a = addonRow(sw) && addonRow(sw).querySelector('a[href]'); return a ? a.href : ''; };

async function pickAddon(sw) {
  if (sw.getAttribute('aria-busy') === 'true') return;
  sw.setAttribute('aria-busy', 'true');
  let m = null;
  try { m = modelFromAjax(await fetchProduct(addonLink(sw))); } catch (err) { m = null; }
  sw.removeAttribute('aria-busy');
  if (!m) { toast(T().strings.cartError); return; }
  if (!m.sizeOptions.length) { sw.dataset.addonSized = 'false'; sw.click(); return; }
  sw.dataset.addonSized = 'true';
  open(sw, { model: m, title: sw.dataset.name, packQty: 1, sel: sw.__pickSel, source: 'addon', onPick: (v, s, mm) => setAddon(sw, v, s, mm) });
}

function setAddon(sw, v, s, m) {
  sw.__pickSel = s.slice();
  sw.dataset.addonId = v.id;
  sw.dataset.price = v.price;
  sw.setAttribute('aria-checked', 'true');
  sw.classList.add('on');
  /* the row says what was chosen, and its price follows the chosen pack */
  const parts = m.sizeOptions.map((o) => s[o.index]).filter((x) => x != null);
  if (m.colorIndex >= 0 && m.options[m.colorIndex].values.length > 1 && s[m.colorIndex] != null) parts.push(s[m.colorIndex]);
  if (v.qty > 1) parts.push(unitLabel(v.qty, m.unit));
  const row = addonRow(sw), name = row && row.querySelector('.addon-name'), info = name && name.parentElement;
  if (info) {
    let note = info.querySelector('[data-addon-choice]');
    if (!note) { note = document.createElement('div'); note.className = 'small addon-choice'; note.setAttribute('data-addon-choice', ''); info.appendChild(note); }
    note.textContent = parts.join(' · ');
    note.hidden = false;
  }
  const pr = sw.parentElement;
  if (pr && pr.classList.contains('pr')) {
    while (pr.firstChild && pr.firstChild !== sw) pr.removeChild(pr.firstChild);
    if (v.compare && v.compare > v.price) { const was = document.createElement('s'); was.textContent = money(v.compare); pr.insertBefore(was, sw); pr.insertBefore(document.createTextNode(' '), sw); }
    pr.insertBefore(document.createTextNode(money(v.price) + ' '), sw);
  }
  track('addon_toggled', { addon: String(v.id), on: true, source: 'sheet' });
  const pdp = window.NOCRY && window.NOCRY.pdp;
  if (pdp && pdp.render) pdp.render();
}

/* runs before the product page's own handler: a switch being turned on is held until its size is known */
document.addEventListener('click', (e) => {
  const sw = e.target.closest && e.target.closest('.buy [data-addon] [data-addon-id]');
  if (!sw || sw.dataset.addonSized === 'false') return;
  if (sw.getAttribute('aria-checked') === 'true') {
    const note = addonRow(sw).querySelector('[data-addon-choice]'); if (note) note.hidden = true;
    return;
  }
  e.stopPropagation(); e.preventDefault();
  pickAddon(sw);
}, true);
function prefetchAddons() { $$('.buy [data-addon] [data-addon-id]').forEach((sw) => { fetchProduct(addonLink(sw)).catch(() => {}); }); }
if (document.readyState === 'complete') setTimeout(prefetchAddons, 1500);
else window.addEventListener('load', () => setTimeout(prefetchAddons, 1500), { once: true });

document.addEventListener('click', (e) => {
  const t = e.target.closest('[data-sheet-open]');
  if (t) { e.preventDefault(); e.stopPropagation(); open(t); return; }
  const sh = sheet(); if (!sh || !sh.contains(e.target)) return;
  if (e.target === sh || e.target.closest('[data-sheet-close]')) { close(); return; }
  if (e.target.closest('[data-sheet-atc]')) { addToCart(); }
});
document.addEventListener('keydown', (e) => {
  const t = e.target.closest && e.target.closest('[data-sheet-open]');
  if (t && (e.key === 'Enter' || e.key === ' ') && t.tagName !== 'BUTTON') { e.preventDefault(); open(t); }
});
document.addEventListener('change', (e) => {
  const sh = sheet(); if (!sh || !sh.contains(e.target)) return;
  const input = e.target.closest('input[data-opt]'); if (!input) return;
  sel[parseInt(input.dataset.opt, 10)] = input.value;
  render();
});
window.NOCRY = Object.assign(window.NOCRY || {}, { sheet: { open, close, fromAjax: modelFromAjax } });
